import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useMemo } from 'react';
import { listAggregatedEvents } from '../services/api/eventAggregationService';
import { API_CONFIG } from '../config/appConfig';
import { useSettings } from '../contexts/settingsContext';

// Performance tracking - measure API call reduction
let apiCallCount = 0;

/**
 * Shared hook for fetching and caching aggregated events
 * Prevents N+1 query pattern by caching events across all components
 * NOTE: Backend enforces MAX_LIMIT=100, so requesting more will be capped
 * Applies user-configurable event limit to prevent performance issues
 */
export function useAggregatedEvents(options = {}) {
  const { limit = API_CONFIG.QUERY_LIMITS.AGGREGATED_EVENTS, enabled = true } = options;
  const { aggregatorEventLimit } = useSettings();

  const query = useQuery({
    queryKey: ['aggregatedEvents', limit],
    queryFn: async () => {
      apiCallCount++;
      const startTime = performance.now();
      console.log(`🌐 [API Call #${apiCallCount}] Fetching aggregated events from backend...`);

      const response = await listAggregatedEvents({ limit });

      const duration = (performance.now() - startTime).toFixed(2);
      console.log(`✅ [API Call #${apiCallCount}] Completed in ${duration}ms - ${response.events?.length || 0} events fetched`);

      return response.events || [];
    },
    enabled,
    staleTime: API_CONFIG.CACHE.STALE_TIME,
    cacheTime: API_CONFIG.CACHE.CACHE_TIME,
  });

  // Apply event limit from user settings
  const limitedData = useMemo(() => {
    if (!query.data) return query.data;
    if (query.data.length <= aggregatorEventLimit) return query.data;

    console.log(`⚠️ Event limit applied: ${query.data.length} events → ${aggregatorEventLimit} events (user setting)`);
    return query.data.slice(0, aggregatorEventLimit);
  }, [query.data, aggregatorEventLimit]);

  return {
    ...query,
    data: limitedData
  };
}

/**
 * Hook to invalidate/refresh the events cache
 * Call this after creating, updating, or deleting events
 */
export function useInvalidateEvents() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['aggregatedEvents'] });
  };
}

/**
 * Hook for polling aggregated events with stable polling interval
 * Uses manual setInterval with React Query for caching only (not auto-refetch)
 */
export function usePollingAggregatedEvents(options = {}) {
  const {
    limit = API_CONFIG.QUERY_LIMITS.POLLING_EVENTS,
    enabled = true,
    refetchInterval = API_CONFIG.CACHE.REFETCH_INTERVAL
  } = options;
  const queryClient = useQueryClient();
  const intervalRef = useRef(null);

  const query = useQuery({
    queryKey: ['pollingEvents', limit],
    queryFn: async () => {
      apiCallCount++;
      const startTime = performance.now();
      console.log(`🔄 [Poll #${apiCallCount}] Fetching ${limit} events from backend...`);

      const response = await listAggregatedEvents({ limit });

      const duration = (performance.now() - startTime).toFixed(2);
      console.log(`✅ [Poll #${apiCallCount}] Completed in ${duration}ms - ${response.events?.length || 0} events fetched`);

      // Debug: Log event types
      if (response.events?.length > 0) {
        const eventTypeCounts = response.events.reduce((acc, e) => {
          acc[e.eventType] = (acc[e.eventType] || 0) + 1;
          return acc;
        }, {});
        console.log(`📊 [Poll #${apiCallCount}] Event types:`, eventTypeCounts);
      }

      return {
        events: response.events || [],
        nextToken: response.nextToken || null,
        hasMore: !!response.nextToken
      };
    },
    enabled, // Initial fetch only
    staleTime: Infinity,
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // NO refetchInterval - we'll handle it manually
  });

  // Manual polling with stable interval
  useEffect(() => {
    if (!enabled) return;

    // Don't set up interval if refetchInterval is false/0 (polling disabled)
    if (!refetchInterval || typeof refetchInterval !== 'number') {
      console.log(`[useAggregatedEvents] Polling disabled (refetchInterval: ${refetchInterval})`);
      return;
    }

    // Set up interval for polling
    console.log(`[useAggregatedEvents] Starting poll interval: ${refetchInterval}ms`);
    intervalRef.current = setInterval(() => {
      console.log(`⏰ Poll timer triggered (${refetchInterval}ms interval)`);
      queryClient.invalidateQueries({ queryKey: ['pollingEvents', limit] });
    }, refetchInterval);

    return () => {
      if (intervalRef.current) {
        console.log(`[useAggregatedEvents] Clearing poll interval`);
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, refetchInterval, limit, queryClient]);

  return query;
}
